// Owner select component for forms - allows admins/managers to assign ownership
import { useAssignableUsers } from '../../hooks/useUsers'
import { useAuth } from '../../hooks/useAuth'
import { Avatar } from '../ui/Avatar'
import { useTranslation } from '../../hooks/useTranslation'

interface OwnerSelectProps {
  value?: string | null
  onChange: (ownerId: string | null) => void
  label?: string
  disabled?: boolean
}

export function OwnerSelect({ value, onChange, label, disabled }: OwnerSelectProps) {
  const { user } = useAuth()
  const { data, isLoading } = useAssignableUsers()
  const { t } = useTranslation()
  const users = data?.data || []

  // Only show for admin and sales_manager roles
  const canAssignOwner = user?.role === 'admin' || user?.role === 'sales_manager'

  if (!canAssignOwner) {
    return null
  }

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 w-20 bg-gray-200 rounded mb-1" />
        <div className="h-10 bg-gray-200 rounded" />
      </div>
    )
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">
        {label || t('fields.owner')}
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
      >
        <option value="">{t('common.selectOwner') || 'Select owner...'}</option>
        {users.map((assignableUser) => (
          <option key={assignableUser.id} value={assignableUser.id}>
            {assignableUser.firstName} {assignableUser.lastName} ({assignableUser.email})
          </option>
        ))}
      </select>
    </div>
  )
}

// Inline display version for compact forms
export function OwnerSelectInline({ value, onChange, disabled }: Omit<OwnerSelectProps, 'label'>) {
  const { user } = useAuth()
  const { data, isLoading } = useAssignableUsers()
  const users = data?.data || []

  const canAssignOwner = user?.role === 'admin' || user?.role === 'sales_manager'

  if (!canAssignOwner || isLoading) {
    return null
  }

  const selectedUser = users.find(u => u.id === value)

  return (
    <div className="flex items-center gap-2">
      {selectedUser && (
        <Avatar firstName={selectedUser.firstName} lastName={selectedUser.lastName} size="xs" />
      )}
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm py-1"
      >
        <option value="">Assign to...</option>
        {users.map((assignableUser) => (
          <option key={assignableUser.id} value={assignableUser.id}>
            {assignableUser.firstName} {assignableUser.lastName}
          </option>
        ))}
      </select>
    </div>
  )
}
